import { ReactElement, Suspense, lazy, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth, hasAuthParams } from "react-oidc-context";
import { Box, CircularProgress } from "@mui/material";
import { PATHS } from "shared/constants/paths";
import Navbar from "shared/containers/Navbar";
import {
  appStatusAtom,
  authTokenAtom,
  dashboardSummaryAtom,
  fetchDashboardSummaryAtom,
  fetchUserPreferencesAtom,
  showHomePageAtom,
} from "atoms";
import { useAtomValue, useSetAtom } from "jotai";
import { RELEASE_NOTES_URL } from "shared/constants/urls";
import { AppStatus } from "shared/models";
import MaintenancePage from "shared/components/MaintenancePage";
import { runtimeConfig } from "@/shared/config/runtime-config-loader";

const HomePage = lazy(() => import("features/HomePage"));
const Dashboard = lazy(() => import("features/Dashboard"));
const Groups = lazy(() => import("features/Groups"));
const Users = lazy(() => import("features/Users"));
const Components = lazy(() => import("features/Components"));
const Privacy = lazy(() => import("shared/containers/Privacy"));
const Imprint = lazy(() => import("shared/containers/Imprint"));
const MarkdownRenderer = lazy(
  () => import("shared/containers/MarkdownRenderer")
);
const Registration = lazy(() => import("../Registration"));
const AccessDeniedNotification = lazy(
  () => import("./AccessDeniedNotification")
);
const Copyright = lazy(() =>
  import("@web-applications/allinga-web-components").then((module) => ({
    default: module.Copyright,
  }))
);
const ThirdPartyNotices = lazy(() =>
  import("@web-applications/allinga-web-components").then((module) => ({
    default: module.ThirdPartyNotices,
  }))
);

function Loader(): JSX.Element {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
      }}
    >
      <CircularProgress />
    </Box>
  );
}

/**
 * Routing of Allinga Admin root
 */

function AppRoutes(): ReactElement {
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const auth = useAuth();
  const { isLoading, isAuthenticated } = auth;
  const fetchUserPreferences = useSetAtom(fetchUserPreferencesAtom);
  const fetchDashboardSummary = useSetAtom(fetchDashboardSummaryAtom);
  const authToken = useAtomValue(authTokenAtom);
  const dashboardSummary = useAtomValue(dashboardSummaryAtom);
  const showHomePage = useAtomValue(showHomePageAtom);
  const appStatus = useAtomValue(appStatusAtom);

  const registerToken = new URLSearchParams(location.search).get("token");

  useEffect(() => {
    if (!isLoading && isAuthenticated && !registerToken && authToken) {
      fetchUserPreferences();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, registerToken, authToken]);

  // Ensure we have access type / component scope available even if the user deep-links
  // directly to /groups, /users, or /components and reloads the page.
  useEffect(() => {
    if (
      !isLoading &&
      isAuthenticated &&
      !registerToken &&
      authToken &&
      !dashboardSummary
    ) {
      fetchDashboardSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated, registerToken, authToken, dashboardSummary]);

  // https://github.com/authts/react-oidc-context?tab=readme-ov-file#automatic-sign-in
  useEffect(() => {
    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !hasTriedSignin &&
      !showHomePage
    ) {
      auth.signinRedirect();
      setHasTriedSignin(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, hasTriedSignin]);

  // Short-circuit the entire app when maintenance mode is enabled.
  if (runtimeConfig.maintenanceMode) {
    return <MaintenancePage />;
  }

  // wait for the authentication to be loaded, true or false
  if (isLoading) {
    return <Loader />;
  }

  if (isAuthenticated && appStatus === AppStatus.denied)
    return <AccessDeniedNotification />;

  // auth is not loading anymore, so we can check if the user is authenticated
  return (
    <Suspense fallback={<Loader />}>
      <>
        {!isLoading && window.location.pathname !== PATHS.register && (
          <Navbar />
        )}
        <Routes>
          <Route path={PATHS.register} element={<Registration />} />
          <Route
            path={PATHS.root}
            element={
              isAuthenticated ? (
                appStatus === AppStatus.allowed ? (
                  <Dashboard />
                ) : (
                  <Loader />
                )
              ) : (
                <HomePage />
              )
            }
          />
          <Route
            path={PATHS.groups}
            element={
              isAuthenticated ? <Groups /> : <Navigate to={PATHS.root} />
            }
          />
          <Route
            path={PATHS.users}
            element={isAuthenticated ? <Users /> : <Navigate to={PATHS.root} />}
          />
          <Route
            path={PATHS.components}
            element={
              isAuthenticated ? <Components /> : <Navigate to={PATHS.root} />
            }
          />
          <Route path={PATHS.privacy} element={<Privacy />} />
          <Route path={PATHS.imprint} element={<Imprint />} />

          <Route
            path={PATHS.releaseNotes}
            element={
              isAuthenticated ? (
                <MarkdownRenderer url={RELEASE_NOTES_URL} />
              ) : (
                <Navigate to={PATHS.root} />
              )
            }
          />

          <Route
            path={PATHS.thirdPartyNotices}
            element={<ThirdPartyNotices />}
          />
          <Route path={PATHS.copyright} element={<Copyright />} />

          {/* Redirect any URL to root */}
          <Route path="*" element={<Navigate to={PATHS.root} />} />
        </Routes>
      </>
    </Suspense>
  );
}

export default AppRoutes;
